import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api, { setStoredToken, setStoredRefreshToken } from '../api/axios';
import { emitToast } from '../context/ToastContext';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // On mount, try to validate the session via /auth/me.
  // If the access token is expired, attempt a refresh before giving up.
  // If the refresh succeeds, the 401 interceptor will have already stored the new token.
  useEffect(() => {
    const validateSession = async () => {
      try {
        const response = await api.get('auth/me');
        if (response.data?.data) {
          setUser(response.data.data);
        }
      } catch {
        setStoredToken(null);
        setStoredRefreshToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    validateSession();
  }, []);

  const login = (userData, refreshToken) => {
    setUser(userData);
    if (refreshToken) {
      setStoredRefreshToken(refreshToken);
    }
  };

  const refreshAccessToken = useCallback(async () => {
    const stored = sessionStorage.getItem('su_refresh_token');
    if (!stored) return null;

    try {
      const response = await api.post('auth/refresh', { refreshToken: stored });
      const data = response.data;
      if (data.accessToken) {
        setStoredToken(data.accessToken);
        if (data.refreshToken) {
          setStoredRefreshToken(data.refreshToken);
        }
        if (data.email) {
          setUser(prev => prev ? { ...prev, email: data.email, role: data.role, userId: data.userId } : null);
        }
        return data.accessToken;
      }
    } catch {
      setStoredToken(null);
      setStoredRefreshToken(null);
    }
    return null;
  }, []);

  const logout = async () => {
    try {
      await api.post('auth/logout');
    } catch {
      // Even if the API call fails, clear local state
    }
    setStoredToken(null);
    setStoredRefreshToken(null);
    try { localStorage.removeItem('impersonator'); } catch { /* storage unavailable */ }
    setUser(null);
    window.location.href = '/login';
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, refreshAccessToken, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => useContext(AuthContext);
